/**
 * Podium scene — end-of-game celebration. Top 5 players ranked. The top
 * row is enlarged with a celebration-tinted background; ranks 2-5 use the
 * primary palette accents. Confetti is fired by main.ts on game-over so
 * we don't double-burst here.
 */

import type { Podium } from '../state/types.js';

export interface PodiumHandle {
  el: HTMLDivElement;
  setPodium(p: Podium[]): void;
  setVisible(v: boolean): void;
}

const STYLES = `
  @keyframes wai-podium-title-in {
    0%   { opacity: 0; transform: translateY(-30px) scale(0.85); letter-spacing: 0.4em; }
    60%  { opacity: 1; transform: translateY(4px)   scale(1.04); letter-spacing: 0.16em; }
    100% { opacity: 1; transform: translateY(0)     scale(1.0);  letter-spacing: 0.12em; }
  }
  @keyframes wai-podium-row-in {
    from { opacity: 0; transform: translateX(40px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes wai-podium-crown-pulse {
    0%, 100% { box-shadow: 0 0 30px rgba(34,197,94,0.5), 0 8px 30px rgba(0,0,0,0.4); }
    50%      { box-shadow: 0 0 80px rgba(34,197,94,0.95), 0 8px 30px rgba(0,0,0,0.4); }
  }
  .wai-podium {
    position: absolute; inset: 0;
    display: flex; flex-direction: column; align-items: center; justify-content: flex-start;
    color: var(--sg-text-on-secondary, #FFC43D);
    font-family: 'Oswald', sans-serif;
    padding: 50px 80px 80px 80px;
    background: radial-gradient(ellipse at 50% 25%, rgba(34,197,94,0.18) 0%, var(--sg-secondary, #060CE9) 35%, var(--sg-bg, #0A0E27) 100%);
  }
  .wai-podium-eyebrow {
    font-family: 'Oswald', sans-serif;
    font-size: 26px; letter-spacing: 0.5em;
    color: var(--sg-accent, #00F0FF);
    text-transform: uppercase;
    opacity: 0.85;
    margin-bottom: 6px;
  }
  .wai-podium-title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 180px; line-height: 0.9;
    letter-spacing: 0.12em;
    color: var(--sg-primary, #FFC43D);
    text-shadow: 0 8px 0 var(--sg-secondary, #060CE9), 0 0 60px rgba(255,196,61,0.5);
    animation: wai-podium-title-in 800ms cubic-bezier(0.18, 0.85, 0.4, 1.0) both;
  }
  .wai-podium-list {
    margin-top: 36px;
    display: flex; flex-direction: column;
    gap: 16px;
    width: 100%;
    max-width: 880px;
  }
  .wai-podium-row {
    display: grid;
    grid-template-columns: 90px 1fr auto;
    gap: 22px;
    align-items: center;
    padding: 18px 28px;
    background: rgba(255,196,61,0.14);
    border: 3px solid var(--sg-primary, #FFC43D);
    border-radius: 16px;
    font-family: 'Oswald', sans-serif;
    color: #fff;
    animation: wai-podium-row-in 480ms cubic-bezier(0.18, 0.85, 0.4, 1.0) both;
    box-shadow: 0 6px 24px rgba(0,0,0,0.4);
  }
  .wai-podium-row.r1 {
    background: linear-gradient(90deg, var(--sg-celebration, #22C55E) 0%, rgba(34,197,94,0.6) 100%);
    color: #000;
    border-color: var(--sg-celebration, #22C55E);
    padding: 28px 36px;
    font-size: 1.4em;
    animation: wai-podium-row-in 480ms cubic-bezier(0.18, 0.85, 0.4, 1.0) both,
               wai-podium-crown-pulse 1.8s ease-in-out infinite 480ms;
  }
  .wai-podium-row.r2 { background: linear-gradient(90deg, rgba(217,217,217,0.18), rgba(217,217,217,0.05)); border-color: #C0C0C0; }
  .wai-podium-row.r3 { background: linear-gradient(90deg, rgba(205,127,50,0.22), rgba(205,127,50,0.05)); border-color: #CD7F32; }
  .wai-podium-rank {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 64px; line-height: 1;
    color: var(--sg-primary, #FFC43D);
    text-align: center;
    text-shadow: 0 4px 0 rgba(0,0,0,0.4);
  }
  .wai-podium-row.r1 .wai-podium-rank { color: #000; font-size: 84px; }
  .wai-podium-row.r2 .wai-podium-rank { color: #C0C0C0; }
  .wai-podium-row.r3 .wai-podium-rank { color: #CD7F32; }
  .wai-podium-name {
    font-family: 'Oswald', sans-serif;
    font-size: 46px;
    letter-spacing: 0.04em;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .wai-podium-row.r1 .wai-podium-name { font-size: 64px; font-weight: 700; }
  .wai-podium-points {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 56px;
    color: var(--sg-primary, #FFC43D);
    font-variant-numeric: tabular-nums;
    text-shadow: 0 4px 0 rgba(0,0,0,0.4);
  }
  .wai-podium-row.r1 .wai-podium-points { color: #000; font-size: 76px; }
  .wai-podium-empty {
    margin-top: 60px;
    font-size: 32px;
    color: rgba(255,255,255,0.7);
    font-family: 'Oswald', sans-serif;
    letter-spacing: 0.1em;
  }
`;

function ensureStyles(): void {
  if (document.getElementById('wai-podium-style')) return;
  const s = document.createElement('style');
  s.id = 'wai-podium-style';
  s.textContent = STYLES;
  document.head.appendChild(s);
}

export function createPodium(stage: HTMLElement): PodiumHandle {
  ensureStyles();
  const el = document.createElement('div');
  el.className = 'wai-podium';
  el.setAttribute('data-scene', 'podium');
  el.setAttribute('data-testid', 'wai-podium');

  const eyebrow = document.createElement('div');
  eyebrow.className = 'wai-podium-eyebrow';
  eyebrow.textContent = 'GAME OVER';

  const title = document.createElement('div');
  title.className = 'wai-podium-title';
  title.textContent = 'PODIUM';

  const list = document.createElement('div');
  list.className = 'wai-podium-list';
  list.setAttribute('data-testid', 'wai-podium-list');

  el.appendChild(eyebrow);
  el.appendChild(title);
  el.appendChild(list);
  stage.appendChild(el);

  return {
    el,
    setPodium(rows) {
      list.innerHTML = '';
      if (rows.length === 0) {
        const empty = document.createElement('div');
        empty.className = 'wai-podium-empty';
        empty.textContent = 'No scores this game.';
        list.appendChild(empty);
        return;
      }
      // Stagger the row-in animation so rows cascade in (top first).
      let delay = 200;
      for (const r of rows) {
        const row = document.createElement('div');
        row.className = `wai-podium-row r${r.rank}`;
        row.style.animationDelay = `${delay}ms`;
        delay += 180;
        const rank = document.createElement('div');
        rank.className = 'wai-podium-rank';
        rank.textContent = `#${r.rank}`;
        const name = document.createElement('div');
        name.className = 'wai-podium-name';
        name.textContent = r.display_name;
        const pts = document.createElement('div');
        pts.className = 'wai-podium-points';
        pts.textContent = `${r.total_points.toLocaleString()} pts`;
        row.appendChild(rank);
        row.appendChild(name);
        row.appendChild(pts);
        list.appendChild(row);
      }
    },
    setVisible(v) {
      el.style.display = v ? 'flex' : 'none';
    },
  };
}
